/**
 * Step 5 (final-package) recovery — restore letter package after refresh, login, or direct URL.
 */

import { buildLetterPackageFromRoundDetail } from './disputeWizardResume';

export const FINAL_PACKAGE_STORAGE_KEY = 'dispute_wizard_final_package_v1';
export const ACTIVE_ROUND_STORAGE_KEY = 'dispute_wizard_active_round_id';
export const ACTIVE_PACKAGE_STORAGE_KEY = 'dispute_wizard_active_package_id';

const SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

export function buildFinalPackageUrl({ packageId, roundId } = {}) {
  const params = new URLSearchParams();
  if (packageId) params.set('package_id', String(packageId));
  if (roundId) params.set('round_id', String(roundId));
  const qs = params.toString();
  return `/dispute-wizard/final-package${qs ? `?${qs}` : ''}`;
}

export function letterPreviewRouteForFamily(family) {
  const map = {
    factual: '/dispute-wizard/factual/preview',
    data_breach: '/dispute-wizard/data_breach/preview',
    isolated: '/dispute-wizard/isolated/preview',
    structural: '/dispute-wizard/structural/preview',
    recovery: '/dispute-wizard/recovery/preview',
    metro2: '/dispute-wizard/metro2',
  };
  const key = String(family || 'factual')
    .toLowerCase()
    .replace(/^meth_/, '');
  return map[key] || map.factual;
}

export function saveFinalPackageSnapshot(snapshot) {
  if (!snapshot?.letterPackage?.letters?.length) return;

  const payload = {
    letterPackage: snapshot.letterPackage,
    stepState: snapshot.stepState || {},
    roundId: snapshot.roundId || snapshot.letterPackage?.round_id || null,
    packageId: snapshot.packageId || snapshot.letterPackage?.package_id || null,
    identityAnchor: snapshot.identityAnchor || snapshot.stepState?.identityAnchor || null,
    family: snapshot.family || snapshot.letterType || 'factual',
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(FINAL_PACKAGE_STORAGE_KEY, JSON.stringify(payload));
    if (payload.packageId) {
      localStorage.setItem(ACTIVE_PACKAGE_STORAGE_KEY, String(payload.packageId));
    }
    if (payload.roundId) {
      localStorage.setItem(ACTIVE_ROUND_STORAGE_KEY, String(payload.roundId));
    }
  } catch (error) {
    console.warn('[final package] failed to save snapshot', error);
  }
}

export function loadFinalPackageSnapshot({ packageId, roundId } = {}) {
  try {
    const raw = sessionStorage.getItem(FINAL_PACKAGE_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data?.timestamp || Date.now() - data.timestamp > SNAPSHOT_TTL_MS) {
      sessionStorage.removeItem(FINAL_PACKAGE_STORAGE_KEY);
      return null;
    }
    if (!data?.letterPackage?.letters?.length) return null;

    if (packageId && data.packageId && String(data.packageId) !== String(packageId)) {
      return null;
    }
    if (roundId && data.roundId && String(data.roundId) !== String(roundId)) {
      return null;
    }

    return {
      letterPackage: data.letterPackage,
      stepState: data.stepState || {},
      roundId: data.roundId,
      packageId: data.packageId,
      identityAnchor: data.identityAnchor,
      family: data.family || 'factual',
    };
  } catch {
    return null;
  }
}

function mergeNavState(built, detail) {
  const navState = detail?.round?.wizard_progress?.wizard_navigation_state || {};
  if (!navState?.letterPackage?.letters?.length) {
    return { ...built, source: built.source || 'round_detail' };
  }

  return {
    ...built,
    letterPackage: navState.letterPackage,
    stepState: { ...built.stepState, ...navState },
    identityAnchor:
      navState.identityAnchor ||
      navState.consumerInfo ||
      built.identityAnchor,
    source: 'round_wizard_nav_state',
  };
}

async function fetchRoundPackage(metro2Service, roundId, token, packageId) {
  const detail = await metro2Service.getRound(roundId, token);
  const built = buildLetterPackageFromRoundDetail(detail, { packageId });
  if (!built.letterPackage.letters?.length) return null;
  return mergeNavState(built, detail);
}

async function fetchFromResumeCandidate(
  metro2Service,
  disputeIntelligenceService,
  token,
  preferredPackageId,
  preferredRoundId,
) {
  const resume = await disputeIntelligenceService.getResumeCandidate(token);
  if (!resume?.has_resume || !resume?.candidate?.round_id) return null;

  const candidate = resume.candidate;
  const roundId = preferredRoundId || candidate.round_id;
  const packageId = preferredPackageId || candidate.package_id;

  const detail = await metro2Service.getRound(roundId, token);
  const built = buildLetterPackageFromRoundDetail(detail, { packageId });
  if (!built.letterPackage.letters?.length) return null;

  const navState = detail?.round?.wizard_progress?.wizard_navigation_state || {};
  const merged = mergeNavState(built, detail);
  return { ...merged, source: 'backend_resume' };
}

/**
 * Recover Step 5 wizard state using fallback order:
 * 1. location.state
 * 2. sessionStorage snapshot
 * 3. URL round_id / package_id (backend round fetch)
 * 4. backend resume candidate + stored active round/package hints
 */
export async function recoverFinalPackageWizardState({
  location,
  metro2Service,
  disputeIntelligenceService,
  token,
  userId,
}) {
  const urlParams = new URLSearchParams(location.search || '');
  const urlPackageId = urlParams.get('package_id');
  const urlRoundId = urlParams.get('round_id');

  if (location.state?.letterPackage?.letters?.length) {
    const state = location.state;
    return {
      source: 'location_state',
      letterPackage: state.letterPackage,
      stepState: state,
      roundId: state.roundId || state.letterPackage?.round_id,
      packageId: state.packageId || state.package_id || state.letterPackage?.package_id,
      identityAnchor:
        state.identityAnchor ||
        state.consumerInfo ||
        state.letterPackage?.consumer_info,
      family: state.letterType || state.family || state.letterPackage?.strategy || 'factual',
    };
  }

  const stored = loadFinalPackageSnapshot({ packageId: urlPackageId, roundId: urlRoundId });
  if (stored) {
    return { source: 'session_storage', ...stored };
  }

  if (!token || !userId) {
    return null;
  }

  if (urlRoundId) {
    try {
      const fromRound = await fetchRoundPackage(metro2Service, urlRoundId, token, urlPackageId);
      if (fromRound) return fromRound;
    } catch (error) {
      console.warn('[STEP 5 RECOVERY] round fetch failed', error);
    }
  }

  const hintedRoundId = urlRoundId || localStorage.getItem(ACTIVE_ROUND_STORAGE_KEY);
  const hintedPackageId = urlPackageId || localStorage.getItem(ACTIVE_PACKAGE_STORAGE_KEY);

  try {
    const fromResume = await fetchFromResumeCandidate(
      metro2Service,
      disputeIntelligenceService,
      token,
      hintedPackageId,
      hintedRoundId,
    );
    if (fromResume) return fromResume;
  } catch (error) {
    console.warn('[STEP 5 RECOVERY] resume candidate failed', error);
  }

  if (hintedRoundId && hintedRoundId !== urlRoundId) {
    try {
      const fromHint = await fetchRoundPackage(metro2Service, hintedRoundId, token, hintedPackageId);
      if (fromHint) return fromHint;
    } catch (error) {
      console.warn('[STEP 5 RECOVERY] hinted round fetch failed', error);
    }
  }

  return null;
}
